import { Toast as BaseToast } from '@base-ui/react/toast';
import type { ComponentProps } from 'react';
import { CrossIcon } from '../lib/icons.tsx';
import { LAYER } from '../lib/layer.ts';
import { cx } from '../lib/styled.tsx';

/**
 * The stack geometry, lifted from Base UI's own recipe.
 *
 * Collapsed, each toast sits a little behind and below the one in front of it
 * (`--toast-index` counts back from the newest); expanded, they fan out by a
 * fixed gap. `--toast-swipe-movement-*` tracks a drag in progress so a
 * half-dismissed toast follows the finger instead of snapping.
 */
const stack =
  '[--gap:0.75rem] [--peek:0.75rem] ' +
  '[--scale:calc(max(0,1-(var(--toast-index)*0.1)))] [--shrink:calc(1-var(--scale))] ' +
  '[--height:var(--toast-frontmost-height,var(--toast-height))] ' +
  '[--offset-y:calc(var(--toast-offset-y)*-1+calc(var(--toast-index)*var(--gap)*-1)+var(--toast-swipe-movement-y))] ' +
  'absolute right-0 bottom-0 left-auto z-[calc(1000-var(--toast-index))] w-full origin-bottom ' +
  'h-[var(--height)] data-expanded:h-[var(--toast-height)] ' +
  '[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--peek))-(var(--shrink)*var(--height))))_scale(var(--scale))] ' +
  'data-expanded:[transform:translateX(var(--toast-swipe-movement-x))_translateY(var(--offset-y))] ' +
  // A gap-sized pad under each toast keeps the pointer inside the stack while moving between them.
  "after:absolute after:top-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-[''] " +
  '[transition:transform_0.5s_var(--ease-out-quint),opacity_0.5s,height_0.15s]';

/** Where a toast goes when it leaves: off the side it was swiped, or straight down. */
const exit =
  'data-starting-style:[transform:translateY(150%)] ' +
  'data-ending-style:opacity-0 data-limited:opacity-0 ' +
  '[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(150%)] ' +
  'data-ending-style:data-[swipe-direction=down]:[transform:translateY(calc(var(--toast-swipe-movement-y)+150%))] ' +
  'data-ending-style:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))] ' +
  'data-ending-style:data-[swipe-direction=left]:[transform:translateX(calc(var(--toast-swipe-movement-x)-150%))_translateY(var(--offset-y))] ' +
  'data-ending-style:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]';

export type ToasterProps = Omit<ComponentProps<typeof BaseToast.Viewport>, 'children'>;

/**
 * The stack of notifications, rendered once near the root of the app inside a
 * {@link ToastProvider}. Everything else pushes to it through {@link useToast}.
 */
export function Toaster({ className, ...props }: ToasterProps) {
  const { toasts } = BaseToast.useToastManager();

  return (
    <BaseToast.Portal>
      <BaseToast.Viewport
        className={cx(
          `${LAYER.toast} fixed right-4 bottom-4 w-[calc(100vw-2rem)] sm:right-8 sm:bottom-8 sm:w-[22.5rem]`,
          className,
        )}
        {...props}
      >
        {toasts.map((toast) => (
          <BaseToast.Root
            key={toast.id}
            toast={toast}
            className={cx(
              stack,
              exit,
              'rounded-card bg-surface-raised text-ink shadow-modal select-none',
            )}
          >
            <BaseToast.Content className="flex items-start gap-3 overflow-hidden p-4 transition-opacity duration-250 ease-out-quint data-behind:opacity-0 data-expanded:opacity-100">
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <BaseToast.Title className="font-display text-md font-bold tracking-tight" />
                <BaseToast.Description className="text-bodysm leading-relaxed text-ink-muted" />
                {toast.actionProps ? (
                  <BaseToast.Action className="mt-1 w-fit text-bodysm font-semibold text-brand-800 underline-offset-4 hover:underline" />
                ) : null}
              </div>
              <BaseToast.Close
                aria-label="Close"
                className="flex size-[30px] shrink-0 items-center justify-center rounded-sm text-ink-faint hover:bg-surface-muted hover:text-ink"
              >
                <CrossIcon className="size-3.5" />
              </BaseToast.Close>
            </BaseToast.Content>
          </BaseToast.Root>
        ))}
      </BaseToast.Viewport>
    </BaseToast.Portal>
  );
}

/** Holds the queue. Wrap the app in it, then render one {@link Toaster} inside. */
export const ToastProvider = BaseToast.Provider;

/** `add`, `update`, `close`, `promise`, and the live list of toasts. */
export const useToast = BaseToast.useToastManager;

/** A queue that lives outside React, for pushing toasts from plain modules. */
export const createToastManager = BaseToast.createToastManager;
